import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { initializeApp, getApps } from 'firebase/app'
import { getDatabase, ref, set, get } from 'firebase/database'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { tournamentsAPI } from '../api/api'

// ─── Firebase ─────────────────────────────────────────────────────────────────
const FIREBASE_CONFIG = {
    apiKey:            'AIzaSyD10olyURDLvM2BdtkLTnwVVB98qfNiDcc',
    authDomain:        'clubswap-5a74a.firebaseapp.com',
    databaseURL:       'https://clubswap-5a74a-default-rtdb.firebaseio.com',
    projectId:         'clubswap-5a74a',
    storageBucket:     'clubswap-5a74a.firebasestorage.app',
    messagingSenderId: '274838532963',
    appId:             '1:274838532963:web:4044ba76f6192dcfdf5956',
}

let _app, _db, _auth
function getDB() {
    if (!_db) {
        _app  = getApps().length ? getApps()[0] : initializeApp(FIREBASE_CONFIG)
        _db   = getDatabase(_app)
        _auth = getAuth(_app)
    }
    return _db
}
function getFirebaseAuth() { getDB(); return _auth }

// ─── Bot API ──────────────────────────────────────────────────────────────────
const BOT_API = 'http://localhost:3001'
async function notifyUser(uid, message) {
    try {
        await fetch(`${BOT_API}/api/notify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid, message })
        })
    } catch (e) { console.warn('Bot notify failed:', e.message) }
}

// ─── Constants ────────────────────────────────────────────────────────────────
const PLATFORM_FEE_PCT = 0.15
const HOST_BONUS_PCT   = 0.05
const PLAYER_OPTIONS   = [4, 6, 8]
const STAKE_OPTIONS    = [50, 100, 200, 500]

const PRIZE_SPLITS = {
    winner: [{ rank: 1, label: '1st place', pct: 1.0 }],
    top3:   [
        { rank: 1, label: '1st place', pct: 0.50 },
        { rank: 2, label: '2nd place', pct: 0.30 },
        { rank: 3, label: '3rd place', pct: 0.20 },
    ],
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function calcPool(stake, playerCount) {
    const gross     = stake * playerCount
    const fee       = Math.round(gross * PLATFORM_FEE_PCT)
    const net       = gross - fee
    const hostBonus = Math.round(gross * HOST_BONUS_PCT)
    return { gross, fee, net, hostBonus }
}

function calcPrizes(net, prizeMode) {
    return PRIZE_SPLITS[prizeMode].map(s => ({
        ...s,
        amount: Math.round(net * s.pct),
    }))
}

function fmt(n) { return Number(n).toLocaleString() }

function initials(name) {
    if (!name) return '?'
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function Tournament() {
    const navigate = useNavigate()

    // Auth
    const [currentUser, setCurrentUser] = useState(null)
    const [authReady, setAuthReady]     = useState(false)
    const [userDiamonds, setUserDiamonds] = useState(0)
    const [displayName, setDisplayName] = useState('')

    // Form state
    const [name, setName]           = useState('')
    const [publisher, setPublisher] = useState('')
    const [arenaName, setArenaName]   = useState('')
    const [externalTournamentId, setExternalTournamentId] = useState('')
    const [gameType, setGameType]   = useState('pool')
    const [stake, setStake]         = useState(100)
    const [maxPlayers, setMaxPlayers] = useState(8)
    const [prizeMode, setPrizeMode] = useState('top3')
    const [customStake, setCustomStake] = useState('')

    // UI
    const [loading, setLoading]   = useState(false)
    const [error, setError]       = useState('')
    const submittingRef = useRef(false)

    // Derived
    const effectiveStake = customStake ? Number(customStake) : stake
    const { gross, fee, net, hostBonus } = calcPool(effectiveStake, maxPlayers)
    const prizes = calcPrizes(net, prizeMode)

    // ── Auth listener ─────────────────────────────────────────
    useEffect(() => {
        const auth = getFirebaseAuth()
        const unsub = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user)
            setAuthReady(true)
            if (user) {
                const db = getDB()
                const balSnap = await get(ref(db, `users/${user.uid}/balance`))
                setUserDiamonds(balSnap.val()?.diamonds ?? 0)

                const profileSnap = await get(ref(db, `users/${user.uid}/profile`))
                const name = profileSnap.val()?.displayName
                    || user.displayName
                    || user.email?.split('@')[0]
                    || 'Player'
                setDisplayName(name)
            }
        })
        return () => unsub()
    }, [])

    // ── Create tournament ─────────────────────────────────────
    const handleCreate = useCallback(async () => {
        if (submittingRef.current) return
        setError('')

        if (!currentUser) { setError('Please log in to create a tournament.'); return }
        if (!name.trim()) { setError('Tournament name is required.'); return }
        if (!publisher.trim()) { setError('Developer / Publisher name is required.'); return }
        if (effectiveStake < 2) { setError('Minimum stake is 2 💎.'); return }
        if (userDiamonds < effectiveStake) {
            setError(`Not enough 💎. You have ${userDiamonds}, need ${effectiveStake} to enter.`)
            return
        }

        submittingRef.current = true
        setLoading(true)
        try {
            const db = getDB()

            // 1. Save to MongoDB (creates record + deducts wallet + escrows entry fee)
            //    The backend's syncBalance will auto-update the Firebase RTDB balance cache.
            const mongoTournament = await tournamentsAPI.create({
                title:      name.trim(),
                game:       gameType,
                entryFee:   effectiveStake,
                maxPlayers,
                prizeMode:  prizeMode === 'winner' ? 'winner_takes_all' : 'top3',
            })

            // 2. Create Firebase RTDB record using MongoDB _id as the key so that
            //    TournamentList.js (which navigates with _id) and TournamentLobby.js
            //    (which reads RTDB by tid) are always using the same identifier.
            const tid = mongoTournament._id

            await set(ref(db, `tournaments/${tid}`), {
                meta: {
                    tid,
                    mongoId:     tid,   // same as MongoDB _id
                    name:        name.trim(),
                    arenaName:   arenaName.trim(),
                    externalTournamentId: externalTournamentId.trim(),
                    publisher:   publisher.trim(),
                    gameType,
                    hostUid:     currentUser.uid,
                    hostName:    displayName,
                    entryFee:    effectiveStake,
                    maxPlayers,
                    prizeMode,
                    createdAt:   Date.now(),
                    status:      'waiting',
                    finalPlayerCount: null,
                    startedAt:   null,
                    announcedAt: null,
                    settledAt:   null,
                    winners:     null,
                    settled:     false,
                    disputed:    false,
                },
                players: {
                    [currentUser.uid]: {
                        uid:         currentUser.uid,
                        displayName,
                        joinedAt:    Date.now(),
                        isHost:      true,
                        paid:        true,
                        claim:       null,
                    }
                }
            })

            // 3. Set host inMatch presence
            await set(ref(db, `users/${currentUser.uid}/presence/inMatch`), true)

            // 4. Notify host via Telegram — non-blocking
            notifyUser(currentUser.uid,
                `🏆 *Tournament Created!*\n\n` +
                `*${name.trim()}* by ${publisher.trim()}\n` +
                `🎮 ${gameType === 'pool' ? '8 Ball Pool 🎱' : 'Checkers ♟️'}\n` +
                `💎 Entry: *${effectiveStake} diamonds*\n` +
                `👥 Players: *${maxPlayers} max* (min 4 to start)\n` +
                `🏅 Prize: *${prizeMode === 'winner' ? 'Winner Takes All' : 'Top 3 Split'}*\n\n` +
                `Share your Tournament ID with players:\n` +
                `🔑 \`${tid}\`\n\n` +
                `Lobby is open for 15 minutes. Good luck! 🎉`
            )

            // 5. Redirect to list
            navigate('/tournaments')

        } catch (e) {
            console.error('Create tournament error:', e)
            setError(e.message || 'Failed to create tournament. Please try again.')
        }
        setLoading(false)
        submittingRef.current = false
    }, [currentUser, name, publisher, arenaName, gameType, effectiveStake, maxPlayers, prizeMode, userDiamonds, displayName, navigate])

    // ─────────────────────────────────────────────────────────
    //  RENDER
    // ─────────────────────────────────────────────────────────
    if (!authReady) {
        return <div style={s.root}><div style={s.centered}><div style={s.spinner} /></div></div>
    }

    if (!currentUser) {
        return (
            <div style={s.root}>
                <div style={s.centered}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>🏆</div>
                    <div style={s.bigLabel}>Create a Tournament</div>
                    <div style={s.subLabel}>Please log in to host a tournament.</div>
                    <button style={s.goldBtn} onClick={() => navigate('/account')}>Log In</button>
                </div>
            </div>
        )
    }

    return (
        <div style={s.root}>
            <div style={s.page}>

                {/* Header */}
                <div style={s.header}>
                    <button style={s.backIcon} onClick={() => navigate('/')}>←</button>
                    <div>
                        <div style={s.headerTitle}>Create Tournament</div>
                        <div style={s.headerSub}>Set up your lobby, stake & prize structure</div>
                    </div>
                </div>

                {/* Balance */}
                <div style={s.balanceBadge}>
                    <span style={s.balanceLabel}>Your balance</span>
                    <span style={s.balanceValue}>💎 {userDiamonds}</span>
                </div>

                {/* ── Game details ── */}
                <div style={s.card}>
                    <div style={s.secLabel}>Game Details</div>

                    <div style={s.fieldGroup}>
                        <label style={s.fieldLabel}>Tournament Name</label>
                        <input
                            style={s.input}
                            placeholder="e.g. Friday Night Pool"
                            value={name}
                            onChange={e => setName(e.target.value)}
                        />
                    </div>

                    <div style={s.fieldGroup}>
                        <label style={s.fieldLabel}>Developer / Publisher</label>
                        <input
                            style={s.input}
                            placeholder="e.g. Miniclip, Playdemic"
                            value={publisher}
                            onChange={e => setPublisher(e.target.value)}
                        />
                    </div>

                    <div style={s.fieldGroup}>
                        <label style={s.fieldLabel}>Table / Arena Name</label>
                        <input
                            style={s.input}
                            placeholder="e.g. Diamond Arena, Friday Table"
                            value={arenaName}
                            onChange={e => setArenaName(e.target.value)}
                        />
                    </div>

                    <div style={s.fieldGroup}>
                        <label style={s.fieldLabel}>Game Tournament ID</label>
                        <div style={{ background: '#0d1520', border: '1px solid #1a3a2a', borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                <span style={{ fontSize: 18 }}>ℹ️</span>
                                <span style={{ fontSize: 12, color: '#4ade80', fontWeight: 600 }}>Where to find your Tournament ID</span>
                            </div>
                            <p style={{ fontSize: 11, color: '#9ca3af', margin: 0, lineHeight: 1.5 }}>
                                Open <strong style={{ color: '#e8f5e9' }}>8 Ball Pool</strong> or <strong style={{ color: '#e8f5e9' }}>Checkers</strong> in the external app and create a tournament there first. The Tournament ID will appear on screen (e.g. <strong style={{ color: '#ffd700' }}>12345678</strong>). Enter it below so players know which room to join in-game.
                            </p>
                        </div>
                        <input
                            style={s.input}
                            placeholder="e.g. 12345678"
                            value={externalTournamentId}
                            onChange={e => setExternalTournamentId(e.target.value)}
                        />
                    </div>

                    <div style={s.fieldGroup}>
                        <label style={s.fieldLabel}>Game</label>
                        <div style={s.gameToggle}>
                            {[
                                { id: 'pool',     label: '🎱 8 Ball Pool' },
                                { id: 'checkers', label: '♟️ Checkers' },
                            ].map(g => (
                                <button
                                    key={g.id}
                                    style={gameType === g.id ? s.gameToggleActive : s.gameToggleBtn}
                                    onClick={() => setGameType(g.id)}
                                >{g.label}</button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Entry stake ── */}
                <div style={s.card}>
                    <div style={s.secLabel}>Entry Stake Per Player</div>
                    <div style={s.stakeGrid}>
                        {STAKE_OPTIONS.map(opt => (
                            <button
                                key={opt}
                                style={stake === opt && !customStake ? s.stakeActive : s.stakeBtn}
                                onClick={() => { setStake(opt); setCustomStake('') }}
                            >
                                <div style={s.stakeAmount}>💎 {opt}</div>
                                <div style={s.stakeLabel}>entry</div>
                            </button>
                        ))}
                    </div>
                    <div style={s.fieldGroup}>
                        <label style={s.fieldLabel}>Custom amount</label>
                        <input
                            style={s.input}
                            type="number"
                            min={10}
                            placeholder="Enter custom stake…"
                            value={customStake}
                            onChange={e => setCustomStake(e.target.value)}
                        />
                    </div>
                    {customStake && Number(customStake) < 2 && (
                        <div style={s.warningMsg}>
                            ⚠️ Minimum stake is 2 💎.
                        </div>
                    )}
                    {userDiamonds < effectiveStake && Number(customStake) >= 2 && (
                        <div style={s.warningMsg}>
                            ⚠️ You need {effectiveStake} 💎 to host. You have {userDiamonds} 💎.
                        </div>
                    )}
                </div>

                {/* ── Max players ── */}
                <div style={s.card}>
                    <div style={s.secLabel}>Max Players</div>
                    <div style={s.subNote}>Min 4 to start · Full 8-player lobby earns host a 5% bonus 🎁</div>
                    <div style={s.playerGrid}>
                        {PLAYER_OPTIONS.map(n => (
                            <button
                                key={n}
                                style={maxPlayers === n ? s.playerActive : s.playerBtn}
                                onClick={() => setMaxPlayers(n)}
                            >
                                <div style={s.playerNum}>{n}</div>
                                <div style={s.playerLabel}>players</div>
                                {n === 8 && <div style={s.bonusTag}>+5% host bonus</div>}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Prize structure ── */}
                <div style={s.card}>
                    <div style={s.secLabel}>Prize Structure</div>
                    <div style={s.prizeToggle}>
                        <button
                            style={prizeMode === 'top3' ? s.prizeActive : s.prizeBtn}
                            onClick={() => setPrizeMode('top3')}
                        >
                            <div style={s.prizeBtnTitle}>Top 3 Split</div>
                            <div style={s.prizeBtnSub}>50% · 30% · 20%</div>
                        </button>
                        <button
                            style={prizeMode === 'winner' ? s.prizeActive : s.prizeBtn}
                            onClick={() => setPrizeMode('winner')}
                        >
                            <div style={s.prizeBtnTitle}>Winner Takes All</div>
                            <div style={s.prizeBtnSub}>100% to 1st place</div>
                        </button>
                    </div>

                    {/* Split rows */}
                    <div style={s.splitRows}>
                        {prizes.map((p, i) => (
                            <div key={p.rank} style={s.splitRow}>
                                <div style={[s.medal, s.medal1, s.medal2, s.medal3][i + 1] || s.medal}>
                                    {['🥇','🥈','🥉'][i]}
                                </div>
                                <div style={s.splitLabel}>{p.label}</div>
                                <div style={s.splitPct}>{Math.round(p.pct * 100)}%</div>
                                <div style={s.splitAmt}>💎 {fmt(p.amount)}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Prize pool summary ── */}
                <div style={s.summaryCard}>
                    <div style={s.secLabel}>Prize Pool Summary</div>

                    <div style={s.summaryRow}>
                        <span>Your entry (host)</span>
                        <span>💎 {fmt(effectiveStake)}</span>
                    </div>
                    <div style={s.summaryRow}>
                        <span>Other players × {maxPlayers - 1}</span>
                        <span>💎 {fmt(effectiveStake)} each</span>
                    </div>
                    <div style={s.summaryRow}>
                        <span>Gross pool</span>
                        <span style={{ fontWeight: 700 }}>💎 {fmt(gross)}</span>
                    </div>
                    <div style={s.summaryRow}>
                        <span style={{ color: '#6a7a8a' }}>Platform fee (15%)</span>
                        <span style={{ color: '#6a7a8a' }}>−💎 {fmt(fee)}</span>
                    </div>
                    <div style={s.divider} />
                    <div style={{ ...s.summaryRow, fontSize: 17, fontWeight: 800, color: '#ffd700' }}>
                        <span>Net prize pool</span>
                        <span>💎 {fmt(net)}</span>
                    </div>

                    {maxPlayers === 8 && (
                        <div style={s.hostBonusRow}>
                            🎁 Host bonus if full lobby + no dispute: <strong>+💎 {fmt(hostBonus)}</strong>
                        </div>
                    )}
                </div>

                {/* ── Recording reminder ── */}
                <div style={s.recordCard}>
                    <span style={{ fontSize: 20 }}>🎥</span>
                    <div>
                        <div style={s.recordTitle}>Recording Reminder</div>
                        <div style={s.recordBody}>
                            Remind all players to record their gameplay before starting.
                            In case of a dispute, recordings will be requested as evidence.
                            Unnecessary disputes are penalised 10 💎.
                        </div>
                    </div>
                </div>

                {error && <div style={s.errorMsg}>{error}</div>}

                {/* ── Create button ── */}
                <button
                    style={loading || userDiamonds < effectiveStake ? s.goldBtnDisabled : s.goldBtn}
                    onClick={handleCreate}
                    disabled={loading || userDiamonds < effectiveStake}
                >
                    {loading
                        ? 'Creating…'
                        : `🔒 Deposit 💎 ${fmt(effectiveStake)} & Open Lobby`}
                </button>

                <button style={s.cancelBtn} onClick={() => navigate('/')}>
                    Cancel
                </button>

            </div>
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────────────────────────────────────
const s = {
    root: { minHeight: '100vh', background: '#070a0f', color: '#e8f5e9', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    page: { maxWidth: 520, margin: '0 auto', padding: '0 16px 100px', display: 'flex', flexDirection: 'column', gap: 12 },
    centered: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 16, padding: 24, textAlign: 'center' },

    // Header
    header: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '16px 0 8px' },
    backIcon: { background: 'none', border: 'none', color: '#ffd700', fontSize: 22, cursor: 'pointer', padding: '4px 8px', marginTop: 2 },
    headerTitle: { fontSize: 20, fontWeight: 800, color: '#e8f5e9' },
    headerSub: { fontSize: 13, color: '#4a7aaa', marginTop: 2 },

    // Balance
    balanceBadge: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#0d1520', border: '1px solid #1a2a3a', borderRadius: 10, padding: '10px 16px' },
    balanceLabel: { fontSize: 12, color: '#4a7aaa' },
    balanceValue: { fontSize: 16, fontWeight: 800, color: '#ffd700' },

    // Card
    card: { background: '#0d1520', border: '1px solid #1a2a3a', borderRadius: 14, padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 12 },
    secLabel: { fontSize: 10, fontWeight: 700, color: '#4a6a8a', letterSpacing: '0.1em', textTransform: 'uppercase' },

    // Fields
    fieldGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
    fieldLabel: { fontSize: 12, color: '#4a7aaa', fontWeight: 600 },
    input: { padding: '11px 14px', borderRadius: 10, border: '1px solid #1a2a3a', background: '#071018', color: '#e8f5e9', fontSize: 14, outline: 'none', boxSizing: 'border-box', width: '100%' },
    subNote: { fontSize: 12, color: '#3a5a7a', marginTop: -4 },

    // Game toggle
    gameToggle: { display: 'flex', gap: 8 },
    gameToggleBtn: { flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #1a2a3a', background: 'transparent', color: '#4a7aaa', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
    gameToggleActive: { flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #ffd700', background: '#1a1a00', color: '#ffd700', cursor: 'pointer', fontSize: 13, fontWeight: 600 },

    // Stake
    stakeGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 },
    stakeBtn: { padding: '10px 4px', borderRadius: 8, border: '1px solid #1a2a3a', background: 'transparent', color: '#4a7aaa', cursor: 'pointer', textAlign: 'center' },
    stakeActive: { padding: '10px 4px', borderRadius: 8, border: '1px solid #ffd700', background: '#1a1a00', color: '#ffd700', cursor: 'pointer', textAlign: 'center' },
    stakeAmount: { fontSize: 13, fontWeight: 700 },
    stakeLabel: { fontSize: 10, color: '#4a6a8a', marginTop: 2 },

    // Players
    playerGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 },
    playerBtn: { padding: '14px 8px', borderRadius: 10, border: '1px solid #1a2a3a', background: 'transparent', color: '#4a7aaa', cursor: 'pointer', textAlign: 'center' },
    playerActive: { padding: '14px 8px', borderRadius: 10, border: '1px solid #ffd700', background: '#1a1a00', color: '#ffd700', cursor: 'pointer', textAlign: 'center' },
    playerNum: { fontSize: 24, fontWeight: 900 },
    playerLabel: { fontSize: 11, color: '#4a6a8a', marginTop: 2 },
    bonusTag: { fontSize: 9, color: '#ffd700', background: '#2a2000', padding: '2px 6px', borderRadius: 8, marginTop: 4, fontWeight: 700 },

    // Prize
    prizeToggle: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
    prizeBtn: { padding: '12px', borderRadius: 10, border: '1px solid #1a2a3a', background: 'transparent', cursor: 'pointer', textAlign: 'left' },
    prizeActive: { padding: '12px', borderRadius: 10, border: '1px solid #ffd700', background: '#1a1a00', cursor: 'pointer', textAlign: 'left' },
    prizeBtnTitle: { fontSize: 13, fontWeight: 700, color: '#ffd700' },
    prizeBtnSub: { fontSize: 11, color: '#8a7a30', marginTop: 2 },

    splitRows: { display: 'flex', flexDirection: 'column', gap: 0 },
    splitRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #0d1520' },
    medal: { fontSize: 18, width: 28, textAlign: 'center', flexShrink: 0 },
    splitLabel: { flex: 1, fontSize: 13, color: '#e8f5e9' },
    splitPct: { fontSize: 13, fontWeight: 700, color: '#ffd700', minWidth: 36, textAlign: 'right' },
    splitAmt: { fontSize: 12, color: '#4ade80', minWidth: 70, textAlign: 'right' },

    // Summary
    summaryCard: { background: 'linear-gradient(135deg, #0a1a00, #0d2000)', border: '1px solid #2a4a00', borderRadius: 14, padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 8 },
    summaryRow: { display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#8a9aaa', padding: '2px 0' },
    divider: { border: 'none', borderTop: '1px solid #1a3a00', margin: '4px 0' },
    hostBonusRow: { background: '#1a1a00', border: '1px solid #3a3000', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#aa9a40', marginTop: 4 },

    // Recording
    recordCard: { background: '#0a1a05', border: '1px solid #2a5a1a', borderRadius: 14, padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' },
    recordTitle: { fontSize: 13, fontWeight: 700, color: '#4ade80', marginBottom: 4 },
    recordBody: { fontSize: 12, color: '#4a7a50', lineHeight: 1.5 },

    // Buttons
    goldBtn: { padding: '15px', borderRadius: 12, background: '#ffd700', border: 'none', color: '#070a0f', fontWeight: 900, fontSize: 16, cursor: 'pointer', textAlign: 'center', marginTop: 4 },
    goldBtnDisabled: { padding: '15px', borderRadius: 12, background: '#3a3000', border: '1px solid #5a5000', color: '#5a5000', fontWeight: 900, fontSize: 16, cursor: 'not-allowed', textAlign: 'center', marginTop: 4 },
    cancelBtn: { padding: '13px', borderRadius: 12, background: 'transparent', border: '1px solid #1a2a3a', color: '#4a7aaa', fontWeight: 600, fontSize: 14, cursor: 'pointer', textAlign: 'center' },

    // Misc
    errorMsg: { background: '#2a0a0a', border: '1px solid #f44336', borderRadius: 8, padding: '10px 14px', color: '#f87171', fontSize: 13 },
    warningMsg: { background: '#2a1a00', border: '1px solid #f59e0b', borderRadius: 8, padding: '8px 12px', color: '#fbbf24', fontSize: 12 },
    bigLabel: { fontSize: 22, fontWeight: 800, color: '#e8f5e9' },
    subLabel: { fontSize: 14, color: '#4a7aaa' },
    spinner: { width: 36, height: 36, borderRadius: '50%', border: '3px solid #1a2a3a', borderTop: '3px solid #ffd700' },
}
